import axios from "axios";
import { API_BASE_URL } from "../../config/apiConfig";
import { getStoredAccessToken } from "../../utils/authSession";

const API = `${API_BASE_URL}/statutory`;

function authHeaders() {
  const token = getStoredAccessToken();
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
      "Content-Type": "application/json",
    },
  };
}

export function formatStatutoryError(err, fallback = "Request failed") {
  const detail = err?.response?.data?.detail ?? err?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || d?.message || JSON.stringify(d)).join("; ");
  }
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  return err?.message || fallback;
}

function unwrap(err) {
  const message = formatStatutoryError(err);
  const wrapped = new Error(message);
  wrapped.detail = err?.response?.data?.detail;
  throw wrapped;
}

export async function fetchStatutoryModule() {
  const { data } = await axios.get(`${API}/module`, authHeaders());
  return data;
}

export async function fetchStatutoryDashboard() {
  const { data } = await axios.get(`${API}/dashboard`, authHeaders());
  return data;
}

export async function fetchIndianStates() {
  const { data } = await axios.get(`${API}/states`, authHeaders());
  return data;
}

export async function saveRegistrations(payload) {
  const { data } = await axios.put(`${API}/registrations`, payload, authHeaders());
  return data;
}

export async function saveEpf(payload) {
  const { data } = await axios.put(`${API}/epf`, payload, authHeaders());
  return data;
}

export async function saveEsi(payload) {
  const { data } = await axios.put(`${API}/esi`, payload, authHeaders());
  return data;
}

export async function savePt(payload) {
  const { data } = await axios.put(`${API}/pt`, payload, authHeaders());
  return data;
}

export async function saveLwf(payload) {
  const { data } = await axios.put(`${API}/lwf`, payload, authHeaders());
  return data;
}

export async function saveBonus(payload) {
  const { data } = await axios.put(`${API}/bonus`, payload, authHeaders());
  return data;
}

/** States with a BrixiGo statutory PT rule — [{ code, name }]. */
export async function fetchPtStates() {
  const { data } = await axios.get(`${API}/pt/states`, authHeaders());
  return data;
}

/** Read-only statutory PT rule (slabs + rule_version) for a state on a date. PT slabs are system managed. */
export async function fetchPtRule(stateCode, asOf) {
  const { data } = await axios.get(`${API}/pt/rules/${stateCode}`, {
    ...authHeaders(),
    params: asOf ? { as_of: asOf } : undefined,
  });
  return data;
}

/** Effective-dated PT configuration history (newest first). */
export async function fetchPtHistory() {
  const { data } = await axios.get(`${API}/pt/history`, authHeaders());
  return data;
}

export async function calcPf(payload) {
  const { data } = await axios.post(`${API}/calculate/pf`, payload, authHeaders());
  return data;
}

export async function calcEsi(payload) {
  const { data } = await axios.post(`${API}/calculate/esi`, payload, authHeaders());
  return data;
}

/** PT preview via the payroll engine's statutory rule — { salary, state_code, as_of? }. Saves nothing. */
export async function calcPt(payload) {
  try {
    const { data } = await axios.post(`${API}/calculate/pt`, payload, authHeaders());
    return data;
  } catch (e) {
    unwrap(e);
  }
}

export async function fetchAuditLogs() {
  const { data } = await axios.get(`${API}/audit-logs`, authHeaders());
  return data;
}

/* ── Advanced PF settings (employee exceptions, VPF, history) ── */

export async function savePfException(payload) {
  try {
    const { data } = await axios.post(`${API}/pf-advanced/exceptions`, payload, authHeaders());
    return data;
  } catch (err) {
    return unwrap(err);
  }
}

export async function saveVpf(payload) {
  try {
    const { data } = await axios.post(`${API}/pf-advanced/vpf`, payload, authHeaders());
    return data;
  } catch (err) {
    return unwrap(err);
  }
}

export async function fetchPfAdvancedHistory({ search = "", page = 1, pageSize = 10 } = {}) {
  const { data } = await axios.get(`${API}/pf-advanced/history`, {
    ...authHeaders(),
    params: { search: search || undefined, page, page_size: pageSize },
  });
  return data;
}

export async function saveDraft(draftPayload) {
  const { data } = await axios.post(`${API}/draft`, { draft_payload: draftPayload }, authHeaders());
  return data;
}

export async function addMinimumWage(payload) {
  try {
    const { data } = await axios.post(`${API}/minimum-wages`, payload, authHeaders());
    return data;
  } catch (e) {
    unwrap(e);
  }
}
